import { Property, Tenant } from '../../types/properties';
import { Home, Users, DollarSign, Calendar, MapPin, Building } from 'lucide-react';
import { formatCurrency } from '../../lib/format';
import { format } from 'date-fns';

interface PropertyCardProps {
  property: Property;
  tenant?: Tenant;
  onClick: () => void;
}

export function PropertyCard({ property, tenant, onClick }: PropertyCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'occupied': return 'text-[var(--t-success)] bg-[var(--t-success-dim)]';
      case 'vacant': return 'text-[var(--t-error)] bg-[var(--t-error)]/10';
      case 'listed-for-rent': return 'text-[var(--t-info)] bg-[var(--t-info-dim)]';
      case 'under-maintenance': return 'text-[var(--t-warning)] bg-[var(--t-warning-dim)]';
      default: return 'text-[var(--t-text-muted)] bg-[var(--t-surface-hover)]';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div
      onClick={onClick}
      className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl overflow-hidden hover:border-[var(--t-primary)] transition-all cursor-pointer shadow-sm hover:shadow-md group flex flex-col"
    >
      {/* Image Area */}
      <div className="h-40 bg-[var(--t-surface-hover)] relative overflow-hidden">
        {property.photo_url ? (
          <img src={property.photo_url} alt={property.address} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[var(--t-text-muted)] gap-2">
            <Building size={32} className="opacity-20" />
            <span className="text-xs uppercase tracking-widest font-bold opacity-50">No Photo</span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <div className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider backdrop-blur-md shadow-sm border border-white/10 ${getStatusColor(property.status)}`}>
            {getStatusLabel(property.status)}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-5 flex flex-col flex-1 gap-4">
        <div>
          <h3 className="font-bold text-lg text-white mb-1 flex items-start gap-2">
            <MapPin size={18} className="text-[var(--t-primary)] shrink-0 mt-1" />
            <span className="line-clamp-1">{property.address}</span>
          </h3>
          <p className="text-sm text-[var(--t-text-muted)] pl-6">
            {property.city}, {property.state} {property.zip}
          </p>
        </div>

        <div className="flex items-center gap-4 text-xs font-medium text-[var(--t-text-secondary)] pl-6">
          {property.beds !== null && <span>{property.beds} Bed</span>}
          {property.beds !== null && property.baths !== null && <span>•</span>}
          {property.baths !== null && <span>{property.baths} Bath</span>}
          {property.baths !== null && property.sqft !== null && <span>•</span>}
          {property.sqft !== null && <span>{property.sqft.toLocaleString()} sqft</span>}
        </div>

        <div className="mt-auto pt-4 border-t border-[var(--t-border-subtle)] grid grid-cols-2 gap-3">
          {tenant && property.status === 'occupied' ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-[var(--t-text-muted)] font-bold">Tenant</span>
                <div className="flex items-center gap-1.5 text-sm text-white font-medium">
                  <Users size={14} className="text-[var(--t-info)]" />
                  <span className="truncate">{tenant.name}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-[var(--t-text-muted)] font-bold">Monthly Rent</span>
                <div className="flex items-center gap-1.5 text-sm text-[var(--t-success)] font-bold">
                  <DollarSign size={14} />
                  {formatCurrency(tenant.monthly_rent || 0)}
                </div>
              </div>
            </>
          ) : (
            <div className="col-span-2 text-sm text-[var(--t-text-muted)] italic text-center py-1">
              No active tenant
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
